import { GridRowId, GridRowModel } from "@mui/x-data-grid-pro";
import { GridApiPro } from "@mui/x-data-grid-pro/models/gridApiPro";
import { get, isArray } from "lodash-es";
import React, { MutableRefObject, useCallback, useMemo, useState } from "react";

import { Identifier } from "react-admin";

import { ACTIONS_COLUMN_ID, CHECK_COLUMN_ID } from "consts";
import { assetType, Maybe, MenuPosition } from "types";

interface MenuState {
  menuPosition: MenuPosition | null;
  isMultiSelection: boolean;
  selectedRows: Maybe<GridRowModel[]>;
  selectedRowsIds: Maybe<GridRowId[]>;
  rowId: Maybe<string>;
  clickedCell: {
    id: Maybe<string>;
    text: Maybe<string>;
  };
}

const initialState: MenuState = {
  menuPosition: null,
  rowId: null,
  isMultiSelection: false,
  selectedRows: null,
  selectedRowsIds: null,
  clickedCell: {
    id: null,
    text: null
  }
};

interface UseDataGridContextMenuProps<DataType> {
  notCopyableColumnsIds?: string[];
  gridApi?: MutableRefObject<GridApiPro>;
  /**
   * Must mirror the getRowId passed to the DataGrid, so the DOM data-id of the
   * clicked row can be mapped back to its record. Defaults to the record id.
   */
  getRowId?: (row: DataType) => GridRowId;
}

export type DataGridContextMenuProviderProps<
  TDataType extends { id: Identifier; tid?: Maybe<Identifier>; type?: unknown }
> = ReturnType<typeof useDataGridContextMenu<TDataType>>;

export const useDataGridContextMenu = <
  DataType extends { id: Identifier; tid?: Maybe<Identifier>; type?: unknown }
>(
  gridData: DataType[] | undefined,
  props?: UseDataGridContextMenuProps<DataType>
) => {
  const { notCopyableColumnsIds, gridApi, getRowId } = props ?? {};

  const resolveRowId = useCallback(
    (row: DataType) => (getRowId ? getRowId(row) : row.id),
    [getRowId]
  );

  const [contextMenuState, setContextMenuState] = useState<MenuState>(initialState);

  const contextMenuRowId = contextMenuState?.rowId;

  const contextMenuRow = useMemo(
    () =>
      contextMenuState
        ? gridData?.find((item) => item.id.toString() === contextMenuState.rowId)
        : null,
    [contextMenuState, gridData]
  );

  const handleOpen = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      event.preventDefault();

      const clickedCellId = (event.target as HTMLElement | null)
        ?.closest(".MuiDataGrid-cell")
        ?.getAttribute("data-field");

      const clickedRowId = (event.target as HTMLElement | null)
        ?.closest(".MuiDataGrid-row")
        ?.getAttribute("data-id");

      const clickedRow = gridData?.find((item) => resolveRowId(item).toString() === clickedRowId);

      const path = clickedCellId as keyof DataType;

      const fieldFromData = get(clickedRow, path);

      const textFromData = isArray(fieldFromData)
        ? fieldFromData.join(" ")
        : fieldFromData?.toString();

      const textFromRow = (event.target as HTMLDivElement).textContent;

      let isMultiSelection = false;
      let selectedRows = null;
      let selectedRowsIds = null;

      if (gridApi?.current) {
        const rows = gridApi.current.getSelectedRows();
        selectedRows = Array.from(rows.values()).filter(Boolean);
        // Record ids, not grid row ids — consumers (edit, history, mark as
        // lost/found) address records by their id.
        selectedRowsIds = selectedRows.map((row) => row.id);

        isMultiSelection = selectedRows.length > 1;
      }

      setContextMenuState({
        menuPosition: {
          mouseX: event.clientX - 2,
          mouseY: event.clientY - 4
        },
        isMultiSelection,
        selectedRows,
        selectedRowsIds,
        rowId: clickedRow ? clickedRow.id.toString() : clickedRowId,
        clickedCell: {
          id: clickedCellId ?? null,
          text: textFromData ?? textFromRow
        }
      });
    },
    [gridApi, gridData, resolveRowId]
  );

  const handleClose = useCallback(() => {
    setContextMenuState((state) => ({ ...state, menuPosition: null }));
  }, []);

  const canCopyText = useMemo(() => {
    const clickedCellId = contextMenuState?.clickedCell.id;

    return !(
      !clickedCellId ||
      clickedCellId === ACTIONS_COLUMN_ID ||
      clickedCellId === CHECK_COLUMN_ID ||
      notCopyableColumnsIds?.includes(clickedCellId)
    );
  }, [contextMenuState?.clickedCell.id, notCopyableColumnsIds]);

  const copyId = () => {
    // Grid row id (the getRowId output), not the record id — for assets and
    // trackers this is the device id (tid on v1 rows, id on v2 rows).
    const rowId = contextMenuRow ? resolveRowId(contextMenuRow).toString() : contextMenuRowId;

    if (rowId) {
      navigator.clipboard.writeText(rowId).catch((err) => console.error("Failed to copy ID:", err));
    }
    handleClose();
  };

  const copySelectedRowsIds = () => {
    const selectedRows = contextMenuState.selectedRows as DataType[] | null;

    if (selectedRows && selectedRows.length > 0) {
      navigator.clipboard
        .writeText(selectedRows.map((row) => resolveRowId(row).toString()).join("\n"))
        .catch((err) => console.error("Failed to copy IDs:", err));
    }

    handleClose();
  };

  const copyCellText = () => {
    const valueFromRow = contextMenuState?.clickedCell.text;

    if (valueFromRow) {
      navigator.clipboard
        .writeText(valueFromRow)
        .catch((err) => console.error("Failed to copy cell text:", err));
    }

    handleClose();
  };

  const copySelectedRowsCellText = () => {
    if (
      contextMenuState.clickedCell.id &&
      contextMenuState.selectedRows &&
      contextMenuState.selectedRows.length > 0
    ) {
      const valuesFromSelectedRows = contextMenuState.selectedRows
        .map((row) => get(row, contextMenuState.clickedCell.id as keyof DataType))
        .map((value) => (isArray(value) ? value.join(" ") : value?.toString()))
        .join("\n");

      navigator.clipboard
        .writeText(valuesFromSelectedRows)
        .catch((err) => console.error("Failed to copy selected rows text:", err));
    }

    handleClose();
  };

  const hasAssignedTracker = !!contextMenuRow?.tid;

  const hasEverySelectedTrackerAssigned = useMemo(
    () => !!contextMenuState?.selectedRows?.every((row) => !!row.tid),
    [contextMenuState?.selectedRows]
  );

  const shouldBlockTrackerActions = contextMenuState.isMultiSelection
    ? !hasEverySelectedTrackerAssigned
    : !hasAssignedTracker;

  const hasMobileAssetType = contextMenuRow?.type === assetType.Mobile;

  const hasAnySelectedMobileAssetType = useMemo(
    () => !!contextMenuState?.selectedRows?.some((row) => row.type === assetType.Mobile),
    [contextMenuState?.selectedRows]
  );

  const shouldBlockMobileAssetActions = contextMenuState.isMultiSelection
    ? hasAnySelectedMobileAssetType
    : hasMobileAssetType;

  const selectedIds = useMemo(() => {
    if (contextMenuState.isMultiSelection) return contextMenuState.selectedRowsIds ?? [];

    return contextMenuRowId ? [contextMenuRowId] : [];
  }, [contextMenuRowId, contextMenuState.isMultiSelection, contextMenuState.selectedRowsIds]);

  const selectedItems = useMemo(() => {
    if (contextMenuState.isMultiSelection) {
      return contextMenuState.selectedRows as DataType[];
    }

    return contextMenuRow ? [contextMenuRow] : [];
  }, [contextMenuRow, contextMenuState.isMultiSelection, contextMenuState.selectedRows]);

  const selectedTids = useMemo(() => {
    if (contextMenuState.isMultiSelection) {
      return (
        contextMenuState.selectedRows
          ?.map((row) => row.tid)
          .filter((tid): tid is string => !!tid) ?? []
      );
    }

    return contextMenuRow?.tid ? [contextMenuRow.tid] : [];
  }, [contextMenuRow?.tid, contextMenuState.isMultiSelection, contextMenuState.selectedRows]);

  return {
    contextMenuState,

    handleClose,
    handleOpen,

    contextMenuRow,
    contextMenuSelectedIds: selectedIds,
    contextMenuSelectedTids: selectedTids,
    contextMenuSelectedItems: selectedItems,
    contextMenuRowId: contextMenuState?.rowId ?? null,
    menuPosition: contextMenuState?.menuPosition ?? null,

    copyId,
    copySelectedRowsIds,
    copyCellText,
    copySelectedRowsCellText,

    canCopyText,
    shouldBlockTrackerActions,
    shouldBlockMobileAssetActions
  };
};
